import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled, {css, keyframes} from 'styled-components';
import {isEqual} from 'lodash';
import {Button, EMPHASIS, Alert} from '@forecast-it/design-system';
import {chatFont, D, skyTokens} from './design-tokens';
import {StatusPill} from './StatusPill';
import {ActionItemRow} from './ActionItemRow';
import {buildEditedValues, isSameEditValue, rowTitle} from './editedValues';
import type {EditedValue} from './editedValues';
import {KeyValueTable} from './KeyValueTable';
import type {KeyValueRow} from './KeyValueTable';
import {ActionPhase, createdTaskIdFor, isTaskCreatePlan} from './actionCardTypes';
import {isTransportFailure} from './actionMessages';
import type {
	ActionPlan,
	ActionResult,
	AffectedItem,
	BillableBasis,
	FieldDescriptor,
	FieldType,
	HistoryProp,
} from './actionCardTypes';
import {validateField} from './fieldValidation';
import {BILLABLE_OVER_DURATION_KEY, PROJECT_ID_ALREADY_IN_USE_KEY} from './KeyValueForm';
import {EVENT_ID, dispatch} from '../../containers/event_manager';
import Util from '../../forecast-app/shared/util/util';
import ValidateProjectIdMutation from '../../mutations/ValidateProjectIdMutation';
import {
	NOVA_CANCEL_STATE,
	toInputType,
	trackNovaActionCancelled,
	trackNovaActionConfirmed,
	trackNovaActionFieldEdited,
	trackNovaActionItemToggled,
	trackNovaActionResolved,
	trackNovaActionResultViewed,
	trackNovaActionRetried,
	type NovaCancelState,
} from '../../tracking/amplitude/novaEvents';

const Card = styled.div<{$failed: boolean}>`
	border: 1px solid ${({$failed}) => ($failed ? D.v2.statusRed : D.assistantBubbleBorder)};
	border-radius: 8px;
	overflow: hidden;
	margin-top: 8px;
`;
const Body = styled.div`
	background: ${D.assistantBubble};
	padding: 16px;
`;
const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	gap: 8px;
`;
// sky/sys/font/heading/4 is byte-identical to the three declarations this used to
// hand-copy: ABC Whyte / 500 / 20px / 24px.
const HeaderTitle = styled.div`
	${skyTokens.font.heading['4']};
	color: ${skyTokens.color.text.content.main};
`;
const Summary = styled.p`
	margin: 0 0 12px;
	${skyTokens.font.body['2']};
	color: ${skyTokens.color.text.content.main};
`;
const Items = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;
const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 16px;
	align-items: center;
	padding: 12px 16px;
	background: ${D.bg};
	border-top: 1px solid ${D.assistantBubbleBorder};
`;
const dotPulse = keyframes`
	0%, 100% { opacity: 1; transform: scale(1); }
	50% { opacity: 0.35; transform: scale(0.65); }
`;

const ConfirmButtonContent = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 8px;
`;

const BlobbingDot = styled.span`
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;
	background: currentColor;
	${css`
		animation: ${dotPulse} 1.2s ease-in-out infinite;
	`}
`;

const BlockedButtonWrapper = styled.div`
	pointer-events: none;
	display: contents;
`;

// Separates the applied / not-applied breakdown from the affected rows above it.
const UnresolvedDetails = styled.div`
	margin-top: 12px;
`;

const CreateTooltip = styled.p`
	margin: 0;
	padding: 8px 12px;
	font-family: ${chatFont};
	font-size: 12px;
	color: ${skyTokens.color.text.content.auxiliary};
	font-style: italic;
`;

const seedEdits = (affected: AffectedItem[]): Record<string, Record<string, unknown>> =>
	Object.fromEntries(
		affected.map(item => [item.id, item.fields ? Object.fromEntries(item.fields.map(f => [f.key, f.value])) : {}])
	);

// Map a resolve-result status → terminal phase. Defined exhaustively so a
// malformed response (missing/unknown status) falls back to Failed rather than
// crashing on `status[0]` and wedging the card in the 'confirming' state.
const STATUS_TO_PHASE: Record<ActionResult['status'], ActionPhase> = {
	done: ActionPhase.Done,
	failed: ActionPhase.Failed,
	partial: ActionPhase.Partial,
	expired: ActionPhase.Expired,
	unresolved: ActionPhase.Unresolved,
};

// What a field's input shows for a stored value, which is also what it commits when it is blurred
// untouched: KeyValueField renders a missing text value as '', a missing number as 0 and a missing
// duration as empty, committed as null.
const asShown = (type: FieldType, value: unknown): unknown => {
	if (type === 'number') return typeof value === 'number' ? value : 0;
	if (type === 'duration') return typeof value === 'number' ? value : null;
	if (type === 'text') return value ?? '';
	return value;
};

// True when a committed value differs from what the input showed, so a blur that changes nothing is
// not an edit, including on a field the proposal left empty.
const isChangedValue = (type: FieldType, before: unknown, after: unknown): boolean =>
	!isSameEditValue(asShown(type, before), asShown(type, after));

type BillableOutcome = {billable: unknown; over: boolean};

// The billable time a time entry update saves, given the card's edits, and whether the server
// refuses it as above the duration. This mirrors `_merge_update_overrides` and its helpers in
// Forecast-MCP's mcp_server/tools/time_registration_write.py; change the two together. The server
// works from the stored entry (`basis`), not the plan's values, and sends billable time the plan
// proposed unless the card changed it. Without a basis the plan's values stand in for the stored
// ones.
// - A read-only billable field means the company has no billable split: billable work saves the
//   whole duration, and other work keeps the billable time it has. The caller does not call this
//   when the duration is read-only too, which means the setting could not be read.
// - Billable time is sent only when it differs from the stored value, whatever the card's overrides
//   hold. Billable time that is not sent, above a changed duration, is capped at it. Sent billable
//   time above the duration is refused. An entry stored above it, with neither value changed, is left.
// - Billable time that is not sent on a fully billable entry is unknown (null) once the duration
//   goes up: the server keeps a separately stored value but extends a derived one, and the card
//   cannot tell the two apart. A task move makes it unknown too.
// A billable time typed back to its original value is not sent, so it follows these rules too.
export const billableOnSave = (
	edits: Record<string, unknown>,
	seed: Record<string, unknown>,
	{billableEditable, basis}: {billableEditable: boolean; basis?: BillableBasis}
): BillableOutcome => {
	const {minutes, billable_minutes: billable} = edits;
	const unchanged = {billable, over: false};
	if (typeof minutes !== 'number' || billable === undefined) return unchanged;
	// With a basis the outcome is always worked out from the stored entry, even with nothing edited.
	if (!basis && isEqual(minutes, seed.minutes) && isEqual(billable, seed.billable_minutes)) return unchanged;
	const stored: BillableBasis = basis ?? {
		minutes: typeof seed.minutes === 'number' ? seed.minutes : null,
		billable_minutes: typeof seed.billable_minutes === 'number' ? seed.billable_minutes : null,
		proposed_billable_minutes: null,
		task_move: false,
	};
	if (!billableEditable) {
		if (stored.task_move) return {billable: null, over: false};
		const fullyBillable = stored.billable_minutes === null || stored.billable_minutes === stored.minutes;
		return {billable: fullyBillable ? minutes : stored.billable_minutes, over: false};
	}
	const cardBillable = typeof billable === 'number' && !isEqual(billable, seed.billable_minutes) ? billable : null;
	let saved = cardBillable ?? stored.proposed_billable_minutes ?? stored.billable_minutes;
	const sent = saved !== null && saved !== stored.billable_minutes;
	let over = false;
	const minutesChanged = minutes !== stored.minutes;
	if (saved !== null && saved > minutes) {
		if (minutesChanged && !sent) saved = minutes;
		else if (minutesChanged || sent) over = true;
	}
	if (stored.task_move) return {billable: null, over};
	const lengthened = stored.minutes !== null && minutes > stored.minutes;
	if (!sent && lengthened && stored.billable_minutes === stored.minutes) return {billable: null, over};
	return {billable: saved, over};
};

// The edits with billable time as the update saves it. Display only: the overrides sent stay what
// the user edited, because sending billable_minutes explicitly changes how the server treats the
// update. Returns `edits` itself when nothing changes.
export const withBillableAsSaved = (
	edits: Record<string, unknown>,
	seed: Record<string, unknown>,
	options: {billableEditable: boolean; basis?: BillableBasis}
): Record<string, unknown> => {
	const {billable} = billableOnSave(edits, seed, options);
	return isEqual(billable, edits.billable_minutes) ? edits : {...edits, billable_minutes: billable};
};

// Counts the fields whose committed value differs from what the proposal showed, by the same test
// onEdit uses. `onlyIds` restricts it to the selected entities, the only ones whose edits are sent.
const countEditedFields = (
	affected: AffectedItem[],
	edits: Record<string, Record<string, unknown>>,
	seed: Record<string, Record<string, unknown>>,
	onlyIds?: Set<string>
): number =>
	affected.reduce((n, item) => {
		if (onlyIds && !onlyIds.has(item.id)) return n;
		const before = seed[item.id] ?? {};
		const after = edits[item.id] ?? {};
		return n + (item.fields ?? []).filter(f => isChangedValue(f.type, before[f.key], after[f.key])).length;
	}, 0);

// The rows of a task-create card that the result maps a created task to, each with that task's
// companyTaskId. Both what counts as applied and what the board highlights come from here.
const createdRowsOf = (affected: AffectedItem[], result: ActionResult | null): Array<[AffectedItem, string]> =>
	affected.flatMap((a): Array<[AffectedItem, string]> => {
		const createdId = createdTaskIdFor(a, result);
		return createdId === null ? [] : [[a, createdId]];
	});

// The affected items a result applied to, matched on either id because the gateway returns
// pre-dedup server ids while `id` may carry a collision suffix. A result that names no ids is taken
// to cover every item, except the ones it lists as failed. On a task-create card the succeeded ids
// are new companyTaskIds that cannot match a row id, so a row counts as applied only when the
// result maps a created task to it.
const itemsAppliedBy = (affected: AffectedItem[], result: ActionResult | null, isTaskCreate: boolean): AffectedItem[] => {
	if (isTaskCreate) return createdRowsOf(affected, result).map(([a]) => a);
	const succeeded = result?.succeededIds && result.succeededIds.length > 0 ? new Set(result.succeededIds) : null;
	const failed = new Set((result?.failed ?? []).map(f => f.id));
	const failedRow = (a: AffectedItem) => failed.has(a.id) || (a.originalId != null && failed.has(a.originalId));
	return affected.filter(
		a => !failedRow(a) && (!succeeded || succeeded.has(a.id) || (a.originalId != null && succeeded.has(a.originalId)))
	);
};

const CANCEL_STATE: Partial<Record<ActionPhase, NovaCancelState>> = {
	[ActionPhase.Pending]: NOVA_CANCEL_STATE.PENDING_REVIEW,
	[ActionPhase.Selected]: NOVA_CANCEL_STATE.SELECTED,
	[ActionPhase.Confirming]: NOVA_CANCEL_STATE.CONFIRMING,
	[ActionPhase.Failed]: NOVA_CANCEL_STATE.FAILED,
	[ActionPhase.Expired]: NOVA_CANCEL_STATE.EXPIRED,
};

export function ActionCard({
	plan,
	onRespond,
	onRegenerateQuery,
	loading = false,
	resolved = false,
	result = null,
	dismissed: dismissedProp = false,
	history,
}: {
	plan: ActionPlan;
	onRespond: (decision: {
		approved: boolean;
		selectedIds: string[];
		/** True when this cancel re-sends one that failed in transport, so a 409 means our own first cancel consumed the plan. */
		retriedCancel?: boolean;
		overrides?: Record<string, Record<string, unknown>>;
		editedValues?: EditedValue[];
	}) => Promise<ActionResult>;
	onRegenerateQuery?: () => void;
	loading?: boolean;
	// Persisted resolution state (from the chat message). On remount / rehydrate
	// from sessionStorage these restore the card's terminal phase so a completed
	// action is NOT re-presented as a fresh, re-confirmable card — which would
	// double-execute the mutation server-side.
	resolved?: boolean;
	result?: ActionResult | null;
	dismissed?: boolean;
	history?: HistoryProp;
}): JSX.Element {
	const {formatMessage} = useIntl();
	// `plan_time_registration_create` does not support partial selection
	// (the create plan has no per-entity exclude semantic on MCP). The
	// gateway also rejects partial selection for this subtype, but the
	// FE pre-selects every row + hides the checkboxes so the user is never
	// presented with an affordance they can't use. (TNSR-247 2026-06-23.)
	const isCreate = plan.entity_subtype === 'create';
	const isTaskCreate = isTaskCreatePlan(plan);
	// Single-entity cards have nothing to multi-select — auto-include the one item
	// and hide its checkbox so Confirm is enabled out of the box, matching the
	// Figma single-entity Action Box.
	const isSingle = plan.affected.length === 1;
	// Time entry delete cards are irreversible: they carry a standing warning and a "Delete" confirm label.
	const isDelete = plan.entity_type === 'time_registration' && plan.entity_subtype === 'delete';
	const isTimeRegistrationUpdate = plan.entity_type === 'time_registration' && plan.entity_subtype === 'update';
	const [selectedIds, setSelectedIds] = useState<Set<string>>(() =>
		isCreate || isSingle ? new Set(plan.affected.map(a => a.id)) : new Set()
	);
	// Restore the terminal phase from a persisted result so a resolved card
	// rehydrated from sessionStorage shows Done/Partial/Unresolved/Failed/Expired
	// rather than a fresh, re-confirmable 'pending' card.
	const [phase, setPhase] = useState<ActionPhase>(() => {
		if (resolved && result) {
			if (result.status === 'done') return ActionPhase.Done;
			if (result.status === 'partial') return ActionPhase.Partial;
			if (result.status === 'expired') return ActionPhase.Expired;
			// Without this case a persisted unresolved result falls through to Failed,
			// which would claim nothing was applied and re-offer Retry on a write that
			// may already have landed.
			if (result.status === 'unresolved') return ActionPhase.Unresolved;
			return ActionPhase.Failed;
		}
		return loading ? ActionPhase.Thinking : ActionPhase.Pending;
	});
	const [dismissed, setDismissed] = useState(dismissedProp);
	const [lastResult, setLastResult] = useState<ActionResult | null>(result ?? null);
	// Re-entrancy latch: a synchronous double-click (or Retry fired before the
	// 'confirming' re-render disables the button) must not issue a second
	// resolve POST and double-execute the mutation.
	const inFlightRef = useRef(false);
	// A cancel is in flight: the card is busy like a confirm, but never reads as Confirming.
	const [cancelling, setCancelling] = useState(false);
	// The decision the last attempt sent, so Retry re-sends that one and never approves a rejection.
	const lastDecisionRef = useRef<'approve' | 'cancel'>('approve');
	// Seed snapshot of the original field values, computed once. seedRef is the
	// immutable baseline for change-detection; edits starts as the same snapshot
	// (onEdit replaces objects immutably, so it never mutates the seed).
	const seedRef = useRef<Record<string, Record<string, unknown>>>(seedEdits(plan.affected));
	const [edits, setEdits] = useState<Record<string, Record<string, unknown>>>(() => seedRef.current);
	// Live "already in use" check for the project_id field (mirrors the live
	// Create Project modal's own onCustomProjectIdBlur) — keyed by entity id
	// since at most one field on any one card triggers this. 'checking' and
	// 'taken' both block Confirm the same way a synchronous constraint
	// violation does; see hasFieldError below.
	const [projectIdCheck, setProjectIdCheck] = useState<Record<string, 'checking' | 'taken' | 'ok'>>({});
	const checkProjectId = (entityId: string, projectId: string): void => {
		if (!projectId) {
			setProjectIdCheck(prev => ({...prev, [entityId]: 'ok'}));
			return;
		}
		setProjectIdCheck(prev => ({...prev, [entityId]: 'checking'}));
		Util.CommitMutation(ValidateProjectIdMutation, {projectId}, (result: {validateProjectId: {valid: string}}) => {
			setProjectIdCheck(prev => ({...prev, [entityId]: result.validateProjectId.valid === 'true' ? 'ok' : 'taken'}));
		});
	};
	const billableFieldOf = (entityId: string): FieldDescriptor | undefined =>
		plan.affected.find(a => a.id === entityId)?.fields?.find(f => f.key === 'billable_minutes');
	// A read-only duration means the server could not read the billable-split setting. Neither value
	// can change then, so the plan's billable time stands and nothing is worked out on the card.
	const billableOptionsOf = (entityId: string): {billableEditable: boolean; basis?: BillableBasis} | undefined => {
		const fields = plan.affected.find(a => a.id === entityId)?.fields;
		const billableField = fields?.find(f => f.key === 'billable_minutes');
		if (fields?.find(f => f.key === 'minutes')?.editable === false) return undefined;
		return billableField && {billableEditable: billableField.editable, basis: billableField.basis};
	};
	// On a task move the target task decides billable time, so it shows as unknown and offers no edit:
	// a value typed there would be sent but could not be shown.
	const displayItemOf = (item: AffectedItem): AffectedItem =>
		isTimeRegistrationUpdate && billableFieldOf(item.id)?.basis?.task_move
			? {...item, fields: item.fields?.map(f => (f.key === 'billable_minutes' ? {...f, editable: false} : f))}
			: item;
	// What each entity's fields show: its edits, with billable time as a time entry update will save
	// it. Derived from the seed on every render, so raising the duration again brings the original
	// billable time back.
	const shownEditsFor = (entityId: string): Record<string, unknown> | undefined => {
		const entityEdits = edits[entityId];
		const options = billableOptionsOf(entityId);
		if (!isTimeRegistrationUpdate || !entityEdits || !options) return entityEdits;
		return withBillableAsSaved(entityEdits, seedRef.current[entityId] ?? {}, options);
	};
	// A time entry's billable time above its duration, which the server refuses, blocks Confirm. As on
	// the server, it counts only once the duration or billable time changed: an entry already stored
	// over the limit can still have its date or notes updated.
	const isBillableOverDuration = (entityId: string): boolean => {
		const options = billableOptionsOf(entityId);
		if (!isTimeRegistrationUpdate || !options) return false;
		return billableOnSave(edits[entityId] ?? {}, seedRef.current[entityId] ?? {}, options).over;
	};
	const onEdit = (entityId: string, key: string, committed: unknown, typed = false): void => {
		// Text, number and duration inputs commit on blur even when untouched; only a change to what
		// the input showed counts as an edit.
		const descriptor = plan.affected.find(a => a.id === entityId)?.fields?.find(f => f.key === key);
		// A time entry update ignores an empty duration, so clearing a duration or typing text that does
		// not read as one undoes the edit and the field shows the plan's value again.
		const value =
			committed == null && isTimeRegistrationUpdate && descriptor?.type === 'duration'
				? seedRef.current[entityId]?.[key]
				: committed;
		// A field showing a derived value commits that value when blurred untouched, which must not
		// turn it into an edit of its own. A value the user typed is an edit even when it matches.
		const shown = shownEditsFor(entityId)?.[key];
		if (descriptor && !typed && shown !== edits[entityId]?.[key] && !isChangedValue(descriptor.type, shown, value)) return;
		// Clearing a field that shows nothing changes nothing, even where the edited value differs.
		if (descriptor && value == null && shown == null) return;
		// Each change to the edited value is tracked, including text that does not read as a value;
		// Confirm's count is the net change from the plan.
		if (
			descriptor &&
			!(committed == null && shown == null) &&
			isChangedValue(descriptor.type, edits[entityId]?.[key], committed)
		) {
			trackNovaActionFieldEdited(plan, {
				fieldName: key,
				inputType: toInputType(descriptor.type),
				hadValidationError: validateField(descriptor, committed) != null,
			});
		}
		setEdits(prev => ({...prev, [entityId]: {...prev[entityId], [key]: value}}));
		if (key === 'project_id') checkProjectId(entityId, String(value ?? ''));
	};

	const effectivePhase: ActionPhase = phase === ActionPhase.Pending && selectedIds.size > 0 ? ActionPhase.Selected : phase;
	const confirming = phase === ActionPhase.Confirming;
	const inFlight = confirming || cancelling;
	const isDone = phase === ActionPhase.Done;
	const isPartial = phase === ActionPhase.Partial;
	const isFailed = phase === ActionPhase.Failed;
	const isExpired = phase === ActionPhase.Expired;
	const isUnresolved = phase === ActionPhase.Unresolved;
	// Unresolved counts as terminal: the write left the building, so the card must
	// stop offering selection and re-confirmation even though the outcome is unknown.
	const isTerminal = isDone || isPartial || isUnresolved || isExpired;
	const boxesDisabled = loading || inFlight || isTerminal;

	const toggle = (id: string): void => {
		if (isCreate || isSingle) return;
		const next = new Set(selectedIds);
		if (next.has(id)) {
			next.delete(id);
		} else {
			next.add(id);
		}
		setSelectedIds(next);
		trackNovaActionItemToggled(plan, next.size);
	};

	const confirm = async (): Promise<void> => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		lastDecisionRef.current = 'approve';
		// Retry reuses this path from the Failed phase; it is its own event, not a second Confirmed.
		if (phase === ActionPhase.Failed) {
			trackNovaActionRetried(plan, lastResult?.guard ?? null);
		} else {
			trackNovaActionConfirmed(plan, {
				selectedCount: selectedIds.size,
				editedFieldCount: countEditedFields(plan.affected, edits, seedRef.current, selectedIds),
			});
		}
		const clickedAt = Date.now();
		setPhase(ActionPhase.Confirming);
		let res: ActionResult;
		try {
			const overrides: Record<string, Record<string, unknown>> = {};
			for (const [entityId, entityEdits] of Object.entries(edits)) {
				// Only emit overrides for entities the user has selected.
				// A deselected entity (in exclude_ids on the server) must not
				// leak its edits into the overrides map.
				if (!selectedIds.has(entityId)) continue;
				const seed = seedRef.current[entityId] ?? {};
				const changed: Record<string, unknown> = {};
				for (const [k, v] of Object.entries(entityEdits)) {
					if (!isSameEditValue(v, seed[k])) changed[k] = v;
				}
				if (Object.keys(changed).length > 0) overrides[entityId] = changed;
			}
			// Omit `overrides` entirely when no fields were changed (truly sparse).
			const hasOverrides = Object.keys(overrides).length > 0;
			res = await onRespond({
				approved: true,
				selectedIds: [...selectedIds],
				...(hasOverrides ? {overrides, editedValues: buildEditedValues(plan.affected, overrides)} : {}),
			});
		} catch (e) {
			// onRespond is expected to resolve (never reject) in production, but a
			// throwing transport must not wedge the card in the 'confirming' state.
			res = {status: 'failed', guard: 'transport', message: e instanceof Error ? e.message : undefined};
		} finally {
			inFlightRef.current = false;
		}
		setLastResult(res);
		// Exactly once per resolve response, from the response itself.
		trackNovaActionResolved(plan, res, {attemptedCount: selectedIds.size, resolveDurationMs: Date.now() - clickedAt});
		setPhase(STATUS_TO_PHASE[res.status] ?? ActionPhase.Failed);
		// Once a task write settles, tell the workflow board (if mounted) to
		// highlight + scroll to the affected task(s) so they're easy to find among many.
		// Only on a fresh confirm() — the rehydrate path never runs here, so a restored
		// card doesn't re-highlight a stale action. `originalId` is the companyTaskId, except on a
		// task-create card, whose rows resolve to the tasks the confirm created.
		if (plan.entity_type === 'task' && (res.status === 'done' || res.status === 'partial')) {
			const ids = isTaskCreate
				? createdRowsOf(plan.affected, res).map(([, createdId]) => createdId)
				: itemsAppliedBy(plan.affected, res, false).map(a => a.originalId ?? a.id);
			if (ids.length > 0) dispatch(EVENT_ID.HIGHLIGHT_TASKS, ids);
		}
		// A placeholder-create write executes out-of-band (server-side via MCP),
		// so the scheduling views — which fetch their own data via REST, not Relay —
		// never learn about it. If the user is on a schedule/timeline view, force a
		// data reload so the new placeholder (and its allocation) appear without a
		// manual refresh. Only on a fresh confirm() (the rehydrate path never runs
		// here). SCHEDULING_RELOAD_DATA re-fetches whichever project/view is mounted,
		// so no project-id match is needed.
		if (plan.entity_type === 'placeholder' && (res.status === 'done' || res.status === 'partial')) {
			if (Util.isScheduling()) dispatch(EVENT_ID.SCHEDULING_RELOAD_DATA);
		}
	};

	// What the card looked like when the user first chose Cancel. A retry of a failed cancel reports
	// the original state, not the Failed card it is retrying from.
	const cancelContextRef = useRef<{stateAtCancel: NovaCancelState; editedFieldCount: number} | null>(null);

	const cancel = async (isRetry = false): Promise<void> => {
		// Don't fire a reject while a confirm POST is already in flight — the
		// resolve is being processed, and a second (approved:false) POST would
		// race the in-flight approval for the same action. The button is also
		// disabled in this phase; the latch covers the synchronous double-click
		// before the disabling re-render lands.
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		lastDecisionRef.current = 'cancel';
		if (isRetry) {
			trackNovaActionRetried(plan, lastResult?.guard ?? null);
		} else {
			cancelContextRef.current = {
				stateAtCancel: CANCEL_STATE[effectivePhase] ?? NOVA_CANCEL_STATE.PENDING_REVIEW,
				editedFieldCount: countEditedFields(plan.affected, edits, seedRef.current),
			};
		}
		setCancelling(true);
		let res: ActionResult;
		try {
			res = await onRespond({approved: false, selectedIds: [], ...(isRetry ? {retriedCancel: true} : {})});
		} catch (e) {
			res = {status: 'failed', guard: 'transport', message: e instanceof Error ? e.message : undefined};
		} finally {
			inFlightRef.current = false;
			setCancelling(false);
		}
		// Cancelled shows only once the gateway has answered; a cancel that never reached it keeps
		// the card open on the failed state, where Retry sends the cancel again.
		if (isTransportFailure(res)) {
			setLastResult(res);
			setPhase(ActionPhase.Failed);
			return;
		}
		// A plan the gateway had already consumed may have been applied, so the card says so
		// instead of claiming a cancellation.
		if (res.status === 'unresolved') {
			setLastResult(res);
			setPhase(ActionPhase.Unresolved);
			return;
		}
		if (cancelContextRef.current) trackNovaActionCancelled(plan, cancelContextRef.current);
		setDismissed(true);
	};

	// Retry sends the decision that failed, so a failed cancel is never turned into an approval.
	const retry = (): Promise<void> => (lastDecisionRef.current === 'cancel' ? cancel(true) : confirm());

	if (dismissed) {
		return (
			<Summary data-testid="action-cancelled">
				{formatMessage({id: 'forecast_agent.action_card.cancelled', defaultMessage: 'Cancelled.'})}
			</Summary>
		);
	}

	const confirmEnabled = effectivePhase === ActionPhase.Selected || (isCreate && plan.affected.length > 0);
	const hasFieldError = plan.affected.some(item => {
		if (!selectedIds.has(item.id)) return false;
		if (!item.fields) return false;
		if (isBillableOverDuration(item.id)) return true;
		return item.fields.some((f: FieldDescriptor) => validateField(f, (edits[item.id] ?? {})[f.key]) !== null);
	});
	// A pending or failed project_id check blocks Confirm the same way a
	// synchronous constraint violation does — confirming while a check is
	// still in flight could submit a value the user is about to be told is
	// unavailable.
	const hasPendingOrFailedProjectIdCheck = plan.affected.some(
		item => selectedIds.has(item.id) && (projectIdCheck[item.id] === 'checking' || projectIdCheck[item.id] === 'taken')
	);
	const confirmEnabledFinal = confirmEnabled && !hasFieldError && !hasPendingOrFailedProjectIdCheck;
	const fieldOverrideErrorsFor = (itemId: string): Record<string, string> | undefined => {
		if (projectIdCheck[itemId] === 'taken') return {project_id: PROJECT_ID_ALREADY_IN_USE_KEY};
		// On a task move billable time cannot be edited, so the error goes on the duration, the field
		// that can clear it.
		if (isBillableOverDuration(itemId))
			return billableFieldOf(itemId)?.basis?.task_move
				? {minutes: BILLABLE_OVER_DURATION_KEY}
				: {billable_minutes: BILLABLE_OVER_DURATION_KEY};
		return undefined;
	};

	// An unresolved outcome names the ids that DID apply and the per-entity failures.
	// Ids are matched against both `id` and `originalId` because the gateway returns
	// pre-dedup server ids, while `id` may carry a collision suffix.
	const titleForId = (rawId: string): string => {
		const item = plan.affected.find(a => a.id === rawId || a.originalId === rawId);
		return item ? rowTitle(item, edits[item.id]) : rawId;
	};
	const unresolvedRows: KeyValueRow[] = [];
	if (isUnresolved) {
		const applied = lastResult?.succeededIds ?? [];
		// A task-create result names new companyTaskIds, which must never be looked up as row
		// ids (those are batch positions). It lists the rows the result maps a created task
		// to, or the new tasks' T-prefixed ids when it maps none.
		const createdTitles = isTaskCreate
			? itemsAppliedBy(plan.affected, lastResult, true).map(a => rowTitle(a, edits[a.id]))
			: [];
		const appliedLabels = !isTaskCreate
			? applied.map(titleForId)
			: createdTitles.length > 0
			? createdTitles
			: applied.map(id => `T${id}`);
		if (appliedLabels.length > 0) {
			unresolvedRows.push({
				key: formatMessage({id: 'forecast_agent.action_card.unresolved_applied', defaultMessage: 'APPLIED'}),
				value: appliedLabels.join(', '),
			});
		}
		const notApplied = lastResult?.failed ?? [];
		if (notApplied.length > 0) {
			unresolvedRows.push({
				key: formatMessage({id: 'forecast_agent.action_card.unresolved_not_applied', defaultMessage: 'NOT APPLIED'}),
				value: notApplied.map(f => `${titleForId(f.id)} — ${f.reason}`).join('; '),
			});
		}
	}
	// A failed outcome carries the same per-entity reasons, and its alert tells the
	// reader to review why each change was skipped — so the card must show them.
	if (isFailed) {
		const skipped = lastResult?.failed ?? [];
		if (skipped.length > 0) {
			unresolvedRows.push({
				key: formatMessage({id: 'forecast_agent.action_card.failed_skipped', defaultMessage: 'SKIPPED'}),
				value: skipped.map(f => `${titleForId(f.id)} — ${f.reason}`).join('; '),
			});
		}
	}
	// The verify handle arrives off the wire, so its tool name is guarded rather than
	// trusted — a non-string would render as "[object Object]" inside the prompt. The
	// handle's `how` text is deliberately not shown: it is prose written to instruct the
	// agent, not the user, so the card falls back to its own generic wording instead.
	const verifyTool =
		typeof lastResult?.verify?.tool === 'string' && lastResult.verify.tool.length > 0 ? lastResult.verify.tool : undefined;
	// The same handle carries `repeat_safe` for confirms whose write is idempotent, so
	// re-applying it changes nothing. The server sends the key only when that holds and
	// omits it otherwise, never sending false, which makes absence the cautious default.
	// The strict `=== true` matters twice over: the value is untrusted wire data, and a
	// truthy string such as "false" would otherwise flip the card into telling the user a
	// non-idempotent change is safe to run twice.
	const isRepeatSafe = lastResult?.verify?.repeat_safe === true;
	// Result Viewed is for a completed item. A row the result did not apply to (a failed or
	// deselected one) still opens without being reported as a result, except on a task-create card,
	// where a row with no created task has nothing to open and shows no View.
	const appliedIds = new Set(itemsAppliedBy(plan.affected, lastResult, isTaskCreate).map(a => a.id));

	return (
		<Card $failed={isFailed}>
			<Body>
				<Header>
					<HeaderTitle>{plan.title}</HeaderTitle>
					<StatusPill phase={effectivePhase} />
				</Header>
				<Summary>{plan.summary}</Summary>
				{isCreate && !isTerminal && (
					<CreateTooltip data-testid="action-create-hint">
						{formatMessage({
							id: 'forecast_agent.action_card.create_no_partial',
							defaultMessage: 'New items can only be confirmed in full — partial selection is not available.',
						})}
					</CreateTooltip>
				)}
				{isDelete && !isTerminal && (
					<Alert
						type={Alert.TYPE.WARNING}
						text={formatMessage({
							id: 'forecast_agent.action_card.delete_irreversible',
							defaultMessage: 'Deleted time entries cannot be restored.',
						})}
					/>
				)}
				<Items>
					{plan.affected.map(item => (
						<ActionItemRow
							key={item.id}
							onViewed={appliedIds.has(item.id) ? () => trackNovaActionResultViewed(plan) : undefined}
							item={displayItemOf(item)}
							checked={selectedIds.has(item.id)}
							disabled={boxesDisabled || isCreate}
							onToggle={toggle}
							// A deleted row has nothing left to open; rows the delete did not apply to keep View.
							showView={(isDone || isPartial) && !(isDelete && appliedIds.has(item.id))}
							history={history}
							entityType={plan.entity_type}
							edits={shownEditsFor(item.id)}
							onEdit={onEdit}
							editsReadOnly={boxesDisabled || isTerminal}
							// Hide the checkbox for `create` (no per-entity selection), single-
							// entity cards (the one item is auto-included), and once the action
							// has settled — terminal states (Done/Partial/Expired) and Failed
							// show row results without a re-selection affordance.
							hideCheckbox={isCreate || isSingle || isTerminal || isFailed}
							fieldOverrideErrors={fieldOverrideErrorsFor(item.id)}
							createdTaskId={isTaskCreate ? createdTaskIdFor(item, lastResult) : undefined}
						/>
					))}
				</Items>
				{(isUnresolved || isFailed) && unresolvedRows.length > 0 && (
					<UnresolvedDetails data-testid="action-unresolved-details">
						<KeyValueTable rows={unresolvedRows} />
					</UnresolvedDetails>
				)}
			</Body>

			{/* Plan-time warnings, rendered before the outcome alerts so they read as
			    something to weigh before confirming rather than as a result. They stay
			    visible once the action settles: a warning like "this will not be visible
			    until the project setting is on" is still true after the write lands. */}
			{plan.warnings.map((warning, index) => (
				<Alert key={`${index}-${warning}`} type={Alert.TYPE.WARNING} text={warning} />
			))}
			{isFailed && (
				<Alert
					type={Alert.TYPE.ERROR}
					text={
						lastResult?.message ||
						formatMessage({
							id: 'forecast_agent.action_card.failed_message',
							defaultMessage: "That didn't work — the request timed out. Your data is unchanged.",
						})
					}
				/>
			)}
			{isPartial && (
				<Alert
					type={Alert.TYPE.WARNING}
					text={
						lastResult?.message ||
						formatMessage(
							isDelete
								? {
										id: 'forecast_agent.action_card.partial_message_delete',
										defaultMessage:
											'{succeededCount, plural, one {# time entry deleted} other {# time entries deleted}}; {failedCount, plural, one {# could not be deleted and was left as it was} other {# could not be deleted and were left as they were}}.',
								  }
								: {
										id: 'forecast_agent.action_card.partial_message',
										defaultMessage:
											'{succeededCount} item(s) updated; {failedCount} could not be updated. Your data for the failed items is unchanged.',
								  },
							{
								succeededCount: lastResult?.succeededIds?.length ?? 0,
								failedCount: lastResult?.failed?.length ?? 0,
							}
						)
					}
				/>
			)}
			{isUnresolved && (
				<>
					<Alert
						type={Alert.TYPE.WARNING}
						text={
							lastResult?.message ||
							formatMessage({
								id: 'forecast_agent.action_card.unresolved_message',
								defaultMessage:
									"We couldn't confirm whether this change was saved. Part of it may already have been applied.",
							})
						}
					/>
					{/* Two axes decide this copy: whether the handle names a read tool, and whether
					    the write is idempotent. Naming the tool is always the better wording, which
					    is why the card writes its own string instead of reusing the server message
					    above. A repeat-safe write must not carry the double-application warning:
					    repeating it is the recovery action that works, and warning against it talks
					    the reader out of the one thing that fixes their problem. The server states
					    what happened and stops there — it deliberately says nothing about whether
					    to retry, because this alert is the only place that decides it. Do not add
					    that sentence back on the server side; it prints here already.

					    "Asking for this again", not "running this again": the footer below is
					    dropped for unresolved, so there is no Retry button on this card. The user's
					    route back is the chat. */}
					<Alert
						type={Alert.TYPE.WARNING}
						text={
							isRepeatSafe
								? verifyTool
									? formatMessage({
											id: 'forecast_agent.action_card.unresolved_verify_repeat_safe_tool',
											defaultMessage:
												'Ask me to look up the current state if you want to confirm what landed. Asking for this again is safe, and only affects what is still outstanding.',
									  })
									: formatMessage({
											id: 'forecast_agent.action_card.unresolved_verify_repeat_safe',
											defaultMessage:
												'Check the current state of the affected items if you want to confirm what landed. Asking for this again is safe, and only affects what is still outstanding.',
									  })
								: isDelete
								? verifyTool
									? formatMessage({
											id: 'forecast_agent.action_card.unresolved_verify_delete_tool',
											defaultMessage:
												'Ask me to look up the current state before trying this again — some of these time entries may already be deleted.',
									  })
									: formatMessage({
											id: 'forecast_agent.action_card.unresolved_verify_delete',
											defaultMessage:
												'Check the current state of these time entries before trying this again — some of them may already be deleted.',
									  })
								: verifyTool
								? formatMessage({
										id: 'forecast_agent.action_card.unresolved_verify_tool',
										defaultMessage:
											'Ask me to look up the current state before trying this again — repeating it could apply the same change twice.',
								  })
								: formatMessage({
										id: 'forecast_agent.action_card.unresolved_verify',
										defaultMessage:
											'Check the current state of the affected items before trying this again — repeating it could apply the same change twice.',
								  })
						}
					/>
				</>
			)}
			{/* The agent re-stages an expired step on its own, so the copy asks nothing of the user. The
			    footer's Regenerate Reply is gated separately (withheld when re-running would repeat a write). */}
			{isExpired && (
				<Alert
					type={Alert.TYPE.WARNING}
					text={formatMessage({
						id: 'forecast_agent.action_card.expired_message_restaged',
						defaultMessage: "This card expired. If Nova doesn't offer it again, ask in the chat.",
					})}
				/>
			)}

			{/* Unresolved drops the whole footer, like Done and Partial: Retry could apply an
			    already-landed write a second time, and Cancel would post a rejection for a
			    decision that has already been dispatched. */}
			{!isDone && !isPartial && !isUnresolved && (
				<Footer>
					<Button
						emphasis={EMPHASIS.LOW}
						disabled={inFlight}
						waiting={cancelling}
						onPress={() =>
							lastDecisionRef.current === 'cancel' && phase === ActionPhase.Failed ? retry() : cancel()
						}
					>
						{formatMessage({id: 'forecast_agent.action_card.cancel', defaultMessage: 'Cancel'})}
					</Button>
					{isExpired ? (
						onRegenerateQuery ? (
							<Button emphasis={EMPHASIS.HIGH} icon={'reload'} onPress={onRegenerateQuery}>
								{formatMessage({
									id: 'forecast_agent.action_card.regenerate_query',
									defaultMessage: 'Regenerate Reply',
								})}
							</Button>
						) : null
					) : isFailed ? (
						<Button
							emphasis={EMPHASIS.HIGH}
							icon={'reload'}
							disabled={inFlight}
							waiting={confirming}
							onPress={retry}
						>
							{formatMessage({id: 'forecast_agent.action_card.retry', defaultMessage: 'Retry'})}
						</Button>
					) : confirming ? (
						<BlockedButtonWrapper>
							<Button emphasis={EMPHASIS.HIGH} onPress={confirm}>
								<ConfirmButtonContent>
									<BlobbingDot />
									{formatMessage({
										id: 'forecast_agent.action_card.status.confirming',
										defaultMessage: 'Confirming',
									})}
								</ConfirmButtonContent>
							</Button>
						</BlockedButtonWrapper>
					) : (
						<Button
							emphasis={EMPHASIS.HIGH}
							icon={isDelete ? 'trash' : 'checkCircle'}
							disabled={!confirmEnabledFinal || cancelling}
							onPress={confirm}
						>
							{isDelete
								? formatMessage({id: 'common.delete', defaultMessage: 'Delete'})
								: formatMessage({
										id: 'forecast_agent.action_card.confirm',
										defaultMessage: 'Confirm Action',
								  })}
						</Button>
					)}
				</Footer>
			)}
		</Card>
	);
}
